/**
 * Shared Audits display pipeline — same logs /audits shows after search,
 * permission, sort, time, compliance, type checks, and cash/units filters.
 * Reporting must use this so charts match the Audits table.
 */

import {
  PERM,
  canUseAuditTools,
  canViewAllAudits,
  isAssociateManager,
  isAssociateUnrestrictedLogType,
} from 'pages/Settings/redux';
import { isReportContextStandingLogType } from 'pages/Reporting/reportContextUsers';
import type { Log } from './hooks';
import {
  filterLogsByAuditsTable,
  logMatchesAuditsTable,
} from './logUtils';
import {
  collectLogSearchQueries,
  filterLogsBySearch,
} from './logSearch';
import type { LogSearchFilter, LogSearchOptions } from './redux';
import {
  buildStaffMaps,
  getLogActorIdentity,
  logMentionsUser,
  resolveActorUser,
  StaffMaps,
} from './logActor';
import { buildCustomerMaps, CustomerMaps } from './logCustomer';
import { formatCombinedSkuDateLabel } from 'utils';

/** Combined-row details list this many unique audit types, then "+N more". */
export const COMBINED_SKU_DETAILS_PREVIEW_MAX = 20;
/** Older combined-row prefix — still parsed for stored report text. */
export const COMBINED_SKU_DETAILS_LEAD = 'Combined';

/** First-seen unique audit names — same SKU is not listed twice. */
export const uniqueCombinedSkuDetails = (names: string[]): string[] => {
  const seen = new Set<string>();
  const unique: string[] = [];
  names.forEach((raw) => {
    const text = String(raw || '').trim();
    if (!text) return;
    const key = text.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    unique.push(text);
  });
  return unique;
};

export type CombinedSkuDetailsParts = {
  count: number,
  names: string[],
  extra: number,
};

export const combinedSkuDetailsParts = (
  auditNames: string[],
  totalCount: number,
  uniqueCount?: number,
): CombinedSkuDetailsParts => {
  const unique = uniqueCombinedSkuDetails(auditNames);
  const names = unique.slice(0, COMBINED_SKU_DETAILS_PREVIEW_MAX);
  const uniqueTotal = uniqueCount != null ? uniqueCount : unique.length;
  const extra = Math.max(0, uniqueTotal - names.length);
  return {
    count: Math.max(totalCount, 1),
    names,
    extra,
  };
};

const logCountWord = (count: number) => `${count} log${count === 1 ? '' : 's'}`;

const splitCombinedSkuTypeList = (raw: string): { names: string[], extra: number } => {
  let rest = String(raw || '').trim();
  let extra = 0;
  const moreTypes = rest.match(/\s+and\s+(\d+)\s+more(?:\s+types?)?$/i);
  const plusMore = rest.match(/,?\s*\+(\d+)\s+more$/i);
  if (moreTypes) {
    extra = Number(moreTypes[1]) || 0;
    rest = rest.slice(0, moreTypes.index).trim();
  } else if (plusMore) {
    extra = Number(plusMore[1]) || 0;
    rest = rest.slice(0, plusMore.index).trim().replace(/,$/, '');
  }
  const names = rest
    ? rest.split(',').map((part) => part.trim()).filter(Boolean)
    : [];
  return { names, extra };
};

/**
 * Combined SKU details — log count, then unique types.
 * Example: "481 logs of 5 types: Malt, Heineken, Soft Drink"
 */
export const formatCombinedSkuDetailsPreview = (
  auditNames: string[],
  totalCount: number,
  uniqueCount?: number,
): string => {
  const { count, names, extra } = combinedSkuDetailsParts(
    auditNames,
    totalCount,
    uniqueCount,
  );
  const countBit = logCountWord(count);
  const uniqueTotal = names.length + extra;
  if (!names.length) return `${count} combined ${count === 1 ? 'log' : 'logs'}`;
  if (uniqueTotal === 1) return `${countBit} of ${names[0]}`;
  const extraBit = extra > 0 ? ` and ${extra} more` : '';
  return `${countBit} of ${uniqueTotal} types: ${names.join(', ')}${extraBit}`;
};

/** Undo formatCombinedSkuDetailsPreview (and the older "Combined N · …" line). */
export const parseCombinedSkuDetailsPreview = (
  text: string,
): CombinedSkuDetailsParts | null => {
  const body = String(text || '').trim();
  if (!body) return null;

  const typed = body.match(/^(\d+)\s+logs? of (\d+) types:\s*(.*)$/i);
  if (typed) {
    const count = Math.max(Number(typed[1]) || 1, 1);
    const { names, extra } = splitCombinedSkuTypeList(typed[3] || '');
    return { count, names, extra };
  }

  const ofOne = body.match(/^(\d+)\s+logs? of (.+)$/i);
  if (ofOne && !/^\d+\s+types:/i.test(ofOne[2])) {
    const count = Math.max(Number(ofOne[1]) || 1, 1);
    const { names, extra } = splitCombinedSkuTypeList(ofOne[2]);
    return { count, names, extra };
  }

  const combinedOnly = body.match(/^(\d+)\s+combined logs?$/i);
  if (combinedOnly) {
    return { count: Math.max(Number(combinedOnly[1]) || 1, 1), names: [], extra: 0 };
  }

  const legacy = body.match(/^Combined\s+(\d+)(?:\s+·\s+(.+))?$/i);
  if (legacy) {
    const count = Math.max(Number(legacy[1]) || 1, 1);
    const { names, extra } = splitCombinedSkuTypeList(legacy[2] || '');
    return { count, names, extra };
  }

  return null;
};

export const combinedSkuDateBounds = (
  dates: Array<string | Date | null | undefined>,
): { start: Date, end: Date } | null => {
  const times = dates
    .map((d) => (d == null || d === '' ? NaN : +new Date(d)))
    .filter((n) => Number.isFinite(n));
  if (!times.length) return null;
  return {
    start: new Date(Math.min(...times)),
    end: new Date(Math.max(...times)),
  };
};

/** Combined SKU date cell — "1 day", "3 days", "1 year 2 months". */
export const formatCombinedSkuDateSpan = (
  dates: Array<string | Date | null | undefined>,
): string => {
  const bounds = combinedSkuDateBounds(dates);
  if (!bounds) return '1 day';
  return formatCombinedSkuDateLabel(bounds.start, bounds.end);
};

/** Numeric amount suitable for Combine SKU rollup (non-empty). */
export const isCombinableSkuAmount = (raw: string | number | null | undefined) => {
  if (raw == null || raw === '' || raw === 'N/A') return false;
  const cleaned = String(raw).replace(/[^0-9.-]/g, '');
  if (!cleaned || cleaned === '-' || cleaned === '.') return false;
  return Number.isFinite(Number(cleaned));
};

/** Cash value suitable for Combine SKU rollup (non-empty, numeric). */
export const isCombinableSkuValueLog = (log: { value?: string | number | null }) => (
  isCombinableSkuAmount(log.value)
);

export type AuditsCombinedSkuMeta = {
  isCombinedSku: true,
  combinedSourceIds: string[],
  combinedLogCount: number,
  sourceDates: string[],
  /** First 20 unique source audit types (SKU names) — combined row only. */
  sourceMessages: string[],
  /** How many unique details exist (may exceed the 20 stored). */
  uniqueSourceCount: number,
};

export type AuditsDisplayLog = Log & Partial<AuditsCombinedSkuMeta>;

/** Units live in activity_logs.data.units and surface as top-level log.units. */
export const getLogUnits = (log: {
  units?: string | number | null,
  data?: { units?: string | number | null },
}) => {
  const raw = log.units ?? log.data?.units;
  if (raw == null || raw === '') return null;
  return raw;
};

export const hasLogUnits = (log: {
  units?: string | number | null,
  data?: { units?: string | number | null },
}) => getLogUnits(log) != null;

/** Roll up cash or unit logs by type label; other rows stay separate. */
export const applyAuditsCombineSku = <T extends Log>(
  logs: T[],
  typeLabelFor: (log: T) => string,
  valueMode: 'value' | 'units' = 'value',
): (T & Partial<AuditsCombinedSkuMeta>)[] => {
  type Bucket = {
    type: string,
    logs: T[],
    sourceDates: string[],
    sourceMessages: string[],
    uniqueSourceCount: number,
    seenDetails: Set<string>,
  };
  const order: string[] = [];
  const buckets = new Map<string, Bucket>();
  const passthrough: (T & Partial<AuditsCombinedSkuMeta>)[] = [];
  const isUnits = valueMode === 'units';

  logs.forEach((log) => {
    const combinable = isUnits
      ? isCombinableSkuAmount(getLogUnits(log))
      : isCombinableSkuValueLog(log);
    if (!combinable) {
      passthrough.push(log);
      return;
    }
    const type = typeLabelFor(log).trim() || String(log.type || 'Audit').trim() || 'Audit';
    let bucket = buckets.get(type);
    if (!bucket) {
      bucket = {
        type,
        logs: [],
        sourceDates: [],
        sourceMessages: [],
        uniqueSourceCount: 0,
        seenDetails: new Set<string>(),
      };
      buckets.set(type, bucket);
      order.push(type);
    }
    bucket.logs.push(log);
    if (log.date) bucket.sourceDates.push(String(log.date));
    const sourceDetail = String(log.type || '').trim();
    if (sourceDetail) {
      const detailKey = sourceDetail.toLowerCase();
      if (!bucket.seenDetails.has(detailKey)) {
        bucket.seenDetails.add(detailKey);
        bucket.uniqueSourceCount += 1;
        if (bucket.sourceMessages.length < COMBINED_SKU_DETAILS_PREVIEW_MAX) {
          bucket.sourceMessages.push(sourceDetail);
        }
      }
    }
  });

  const combinedRows: (T & Partial<AuditsCombinedSkuMeta>)[] = order.flatMap((type) => {
    const bucket = buckets.get(type)!;
    if (bucket.logs.length <= 1) return bucket.logs;
    const combinedSourceIds = bucket.logs
      .map((l) => String(l.id || '').trim())
      .filter(Boolean);
    const summed = bucket.logs.reduce((acc, row) => {
      const raw = isUnits ? getLogUnits(row) : row.value;
      const cleaned = String(raw ?? '').replace(/[^0-9.-]/g, '');
      const n = Number(cleaned);
      return Number.isFinite(n) ? acc + n : acc;
    }, 0);
    const anchor = bucket.logs[0];
    const sortDate = bucket.logs.reduce((latest, row) => {
      const t = +new Date(row.date);
      return Number.isFinite(t) && t > latest ? t : latest;
    }, 0);
    return [{
      ...anchor,
      id: combinedSourceIds[0] || anchor.id,
      type: bucket.type,
      message: bucket.type,
      ...(isUnits ? { units: String(summed) } : { value: String(summed) }),
      date: sortDate ? new Date(sortDate).toISOString() : anchor.date,
      isCombinedSku: true as const,
      combinedSourceIds,
      combinedLogCount: bucket.logs.length,
      sourceDates: bucket.sourceDates,
      sourceMessages: bucket.sourceMessages,
      uniqueSourceCount: bucket.uniqueSourceCount,
    }];
  });

  return [...combinedRows, ...passthrough].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );
};

/** In-house expense lines — hidden from Sessions / compliance audit views. */
export const isExpenseBalanceLog = (log: { type?: string, message?: string }) => (
  log.type === 'Expense'
  || Boolean(log.message && String(log.message).includes('[&Expense&]'))
);

export type AuditsSearchArchives = {
  deletedTypes: Set<string>,
  deletedGroups: Set<string>,
  deletedDepartments: Set<string>,
};

export type AuditsDisplayLogsParams = {
  logs: Log[],
  permission: number,
  staffId?: string | null,
  username?: string | null,
  /** Staff list — used to resolve actor permission for Assoc visibility. */
  users: { id?: string | number | null, username?: string | null, permission?: string | number | null }[],
  logSearches: LogSearchFilter[],
  logSearch: string,
  logSearchOptions: LogSearchOptions,
  staffMaps: StaffMaps,
  customerMaps: CustomerMaps,
  departmentNames: Set<string>,
  searchArchives: AuditsSearchArchives | null,
  checked: string[],
  logGroups: Record<string, string[]>,
  /** When true, keep only logs inside startDate..endDate (Audits time toggle). */
  useTimeRange: boolean,
  startDate?: string | Date | null,
  endDate?: string | Date | null,
  valueMode: 'value' | 'units',
  allItems: boolean,
  cashIn: boolean,
  cashOut: boolean,
  associateElevatedAudits: boolean,
  isComplianceAudit: boolean,
  complianceAuditStaffId?: string | null,
  /** When set, keep only these activity_logs ids (report audit set). */
  onlyLogIds?: string[] | null,
  /** Report jump: keep logs from this id through endLogId (inclusive). */
  startLogId?: string | null,
  endLogId?: string | null,
};

/** True if this row is the log, or a Combine SKU chip that includes it. */
const logRowHasId = (
  log: { id?: string, combinedSourceIds?: string[] },
  id: string,
) => {
  if (String(log.id) === id) return true;
  return (log.combinedSourceIds || []).some((sourceId) => String(sourceId) === id);
};

/** Inclusive slice: start id through end id on the newest-first table list. */
export const sliceLogsByStartEndLogId = <T extends {
  id?: string,
  date?: string,
  combinedSourceIds?: string[],
}>(
  logs: T[],
  startLogId?: string | null,
  endLogId?: string | null,
): T[] => {
  const startId = String(startLogId || '').trim();
  const endId = String(endLogId || '').trim();
  if (!startId && !endId) return logs;
  if (startId && startId === endId) {
    return logs.filter((log) => logRowHasId(log, startId));
  }
  // Newest first: end (later) is above, start (earlier) is below.
  // No start → oldest. No end → newest.
  let startIndex = startId
    ? logs.findIndex((log) => logRowHasId(log, startId))
    : logs.length - 1;
  let endIndex = endId
    ? logs.findIndex((log) => logRowHasId(log, endId))
    : 0;
  // Ids not in the loaded list yet — do not empty the table.
  if (startId && startIndex < 0 && endId && endIndex < 0) return logs;
  if (startId && startIndex < 0) startIndex = logs.length - 1;
  if (endId && endIndex < 0) endIndex = 0;
  const low = Math.min(startIndex, endIndex);
  const high = Math.max(startIndex, endIndex);
  return logs.slice(low, high + 1);
};

export const buildPermissionByActor = (
  users: { id?: string | number | null, username?: string | null, permission?: string | number | null }[],
): Map<string, number> => {
  const map = new Map<string, number>();
  (users || []).forEach((u) => {
    const perm = Number(u.permission) || 0;
    if (u.id != null && String(u.id) !== '') {
      map.set(String(u.id), perm);
    }
    if (!u.username) return;
    map.set(u.username.replace(/\s+/g, '').toLowerCase(), perm);
    map.set(u.username.toLowerCase(), perm);
  });
  return map;
};

type ViewLogShape = {
  type?: string,
  message: string,
  created_by?: string | null,
  units?: string | number | null,
  data?: { units?: string | number | null },
};

/**
 * Same visibility rules as /audits LogTable — Assoc Managers do not see
 * Manager+/Owner Desk reservation (etc.) unless elevate+units unlocks them.
 */
export const canViewAuditLog = (
  log: ViewLogShape,
  opts: {
    permission: number,
    staffId?: string | null,
    username?: string | null,
    staffMaps: StaffMaps,
    permissionByActor: Map<string, number>,
    complianceReviewActive: boolean,
  },
): boolean => {
  const {
    permission,
    staffId,
    username,
    staffMaps,
    permissionByActor,
    complianceReviewActive,
  } = opts;
  const sessionActor = { staffId: staffId || '', username: username || '' };

  // User alert levels from reports — Manager (5) and Owner+ only.
  if (isReportContextStandingLogType(log.type) && !canViewAllAudits(permission)) {
    return false;
  }

  const actorPermission = () => {
    const actor = getLogActorIdentity(log);
    if (!actor) return null;
    const resolved = resolveActorUser(actor, staffMaps);
    return permissionByActor.get(String(actor))
      ?? (resolved?.id != null ? permissionByActor.get(String(resolved.id)) : undefined)
      ?? (resolved?.username
        ? permissionByActor.get(resolved.username.replace(/\s+/g, '').toLowerCase())
        : undefined)
      ?? null;
  };

  // Assoc Managers always see occupancy types (any author).
  if (isAssociateManager(permission) && isAssociateUnrestrictedLogType(log.type)) {
    return true;
  }

  // Compliance → bare Manager+ logs without units stay hidden for everyone.
  if (complianceReviewActive && !hasLogUnits(log)) {
    const actorPerm = actorPermission();
    if (!(actorPerm != null && actorPerm <= PERM.SUB_MANAGER)) {
      return false;
    }
  }

  if (canViewAllAudits(permission)) return true;

  if (isAssociateManager(permission)) {
    const actor = getLogActorIdentity(log);
    if (!actor) return false;
    if (logMentionsUser(log.message, sessionActor)
      || (staffId && String(log.created_by || '') === String(staffId))) {
      return true;
    }
    const actorPerm = actorPermission();
    if (actorPerm == null) return false;
    if (actorPerm <= PERM.SUB_MANAGER) return true;
    return Boolean(complianceReviewActive && hasLogUnits(log));
  }

  return logMentionsUser(log.message, sessionActor)
    || Boolean(staffId && String(log.created_by || '') === String(staffId));
};

/** Full /audits table pipeline (pre-pagination). */
export const buildAuditsDisplayLogs = (params: AuditsDisplayLogsParams): Log[] => {
  const {
    logs,
    permission,
    staffId,
    username,
    users,
    logSearches,
    logSearch,
    logSearchOptions,
    staffMaps,
    customerMaps,
    departmentNames,
    searchArchives,
    checked,
    logGroups,
    useTimeRange,
    startDate,
    endDate,
    valueMode,
    allItems,
    cashIn,
    cashOut,
    associateElevatedAudits,
    isComplianceAudit,
    complianceAuditStaffId,
    onlyLogIds,
    startLogId,
    endLogId,
  } = params;

  const onlyIdSet = onlyLogIds?.length
    ? new Set(onlyLogIds.map((id) => String(id)))
    : null;

  const permissionByActor = buildPermissionByActor(users);
  const complianceReviewActive = Boolean(
    associateElevatedAudits
    && useTimeRange
    && startDate
    && endDate,
  );

  const queries = collectLogSearchQueries(logSearches, logSearch, logSearchOptions);
  const hasLogIdRange = Boolean(
    String(startLogId || '').trim() || String(endLogId || '').trim(),
  );
  const searchBasedLogs = onlyIdSet
    ? (logs || []).filter((l) => onlyIdSet.has(String(l.id)))
    : filterLogsBySearch(
      logs || [],
      queries,
      staffMaps,
      searchArchives,
      departmentNames,
      customerMaps,
    );

  const permissionFiltered = searchBasedLogs.filter((l) => canViewAuditLog(l, {
    permission,
    staffId,
    username,
    staffMaps,
    permissionByActor,
    complianceReviewActive,
  }));

  const dateSortedLogs = [...permissionFiltered];
  dateSortedLogs.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  // Exact report ids are the set. Start/end slice would drop combined siblings.
  const idRangeLogs = onlyIdSet
    ? dateSortedLogs
    : sliceLogsByStartEndLogId(
      dateSortedLogs,
      startLogId,
      endLogId,
    );
  const dateFilteredLogs = useTimeRange && !onlyIdSet && !hasLogIdRange
    ? idRangeLogs.filter((log) => {
      if (!startDate || !endDate) return false;
      return +new Date(log.date) >= +new Date(startDate)
        && +new Date(log.date) <= +new Date(endDate);
    })
    : idRangeLogs;

  const logBelongsToComplianceAuditedUser = (log: {
    message: string,
    created_by?: string | null,
  }) => {
    if (!complianceAuditStaffId) return false;
    return String(log.created_by || '') === String(complianceAuditStaffId);
  };

  let complianceAuditFilteredLogs = dateFilteredLogs;
  if (isComplianceAudit && !onlyIdSet) {
    const matchesCheckedDepartment = (log: {
      type: string,
      department?: string | null,
      inventory_type?: string | null,
    }) => {
      if (!checked.length) return true;
      return logMatchesAuditsTable(log, checked, logGroups);
    };
    let firstAuditedMs: number | null = null;
    dateFilteredLogs.forEach((log) => {
      if (!logBelongsToComplianceAuditedUser(log)) return;
      if (!hasLogUnits(log)) return;
      if (!matchesCheckedDepartment(log)) return;
      if (!(Number(log.value) > 0)) return;
      const t = +new Date(log.date);
      if (Number.isNaN(t)) return;
      if (firstAuditedMs == null || t < firstAuditedMs) firstAuditedMs = t;
    });
    if (firstAuditedMs != null) {
      complianceAuditFilteredLogs = dateFilteredLogs.filter((log) => {
        const t = +new Date(log.date);
        if (Number.isNaN(t) || t < firstAuditedMs!) return true;
        return logBelongsToComplianceAuditedUser(log);
      });
    }
  }

  const isUnitsMode = valueMode === 'units';
  if (onlyIdSet) {
    // Report audit set: permission-filtered ids only — do not re-apply type/cash cuts.
    return complianceAuditFilteredLogs;
  }
  return complianceAuditFilteredLogs.filter((log) => {
    if (complianceReviewActive || isComplianceAudit) {
      if (isExpenseBalanceLog(log)) return false;
      if (!hasLogUnits(log)) return false;
    }
    const isChecked = canUseAuditTools(permission)
      ? logMatchesAuditsTable(log, checked, logGroups)
      : true;
    if (!isChecked) return false;
    if (isUnitsMode) {
      return allItems || hasLogUnits(log);
    }
    const numValue = Number(log.value);
    return allItems || (cashIn && numValue > 0) || (cashOut && numValue < 0);
  });
};

// Re-export helpers Reporting may need when building staff/customer maps outside the hook.
export { buildStaffMaps, buildCustomerMaps };
